/**
 * KAN-685: a remembered filter value that is no longer among the options
 * (stage of a switched-off funnel, removed source code, deleted Telegram
 * account) is dropped back to "all". Otherwise the select shows an empty
 * value while the request still sends it and the list stays empty.
 *
 * A filter is checked only after its options are loaded: while loading or
 * after a failed load nothing is reset.
 */
import { useEffect } from 'react';
import { CLIENT_SOURCE_OPTIONS } from '@/types/client';
import type { FunnelStageConfig } from '@/types/funnel';
import { useDropUnknownManager } from '@/hooks/useManagerFilterChoice';
import { useManagers } from '@/hooks/useManagers';
import {
  ALL_MANAGERS,
  ALL_SENDERS,
  ALL_SOURCES,
  ALL_STAGES,
  NO_MANAGER,
} from './useClientListFilters';
import { stageOptionValue, useFunnelStageOptions } from './useFunnelStageOptions';
import { useSenderAccountOptions } from './useSenderAccountOptions';

interface FilterValues {
  stage: string;
  sender: string;
  source: string;
  /** KAN-744: optional; KAN-909: a deleted manager falls back to the default (dropManager). */
  manager?: string;
  dropManager?: () => void;
  setStage: (value: string) => void;
  setSender: (value: string) => void;
  setSource: (value: string) => void;
}

/**
 * A stage value is known when it equals an option value, or, for a bare
 * codename from a dashboard link without `?funnel=`, when some stage has it.
 */
export function isKnownStage(value: string, stages: FunnelStageConfig[]): boolean {
  if (value.includes(':')) return stages.some((s) => stageOptionValue(s) === value);
  return stages.some((s) => s.codename === value);
}

const CLIENT_SPECIAL_MANAGERS = [ALL_MANAGERS, NO_MANAGER];
const noop = () => undefined;

export function isKnownSource(value: string): boolean {
  return CLIENT_SOURCE_OPTIONS.some((o) => o.value === value);
}

export function useDropStaleFilterValues({
  stage,
  sender,
  source,
  manager,
  dropManager,
  setStage,
  setSender,
  setSource,
}: FilterValues): void {
  const stages = useFunnelStageOptions();
  const senderOptions = useSenderAccountOptions();
  const { data: managers } = useManagers('all');

  useEffect(() => {
    if (!stages || stage === ALL_STAGES || !stage) return;
    if (!isKnownStage(stage, stages)) setStage(ALL_STAGES);
  }, [stages, stage, setStage]);

  useEffect(() => {
    if (!senderOptions || sender === ALL_SENDERS || !sender) return;
    if (!senderOptions.some((o) => o.value === sender)) setSender(ALL_SENDERS);
  }, [senderOptions, sender, setSender]);

  useEffect(() => {
    if (source === ALL_SOURCES || !source) return;
    if (!isKnownSource(source)) setSource(ALL_SOURCES);
  }, [source, setSource]);

  useDropUnknownManager(manager ?? '', dropManager ? managers : undefined, CLIENT_SPECIAL_MANAGERS, dropManager ?? noop);
}
